import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

type AuthCtx = { user: User | null; session: Session | null; loading: boolean; signOut: () => Promise<void> };
const Ctx = createContext<AuthCtx>({ user: null, session: null, loading: true, signOut: async () => {} });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s);
    });
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });

    // Auto-recovery: refresh a stale session when the tab wakes up or the
    // network comes back, so users never hit "not authenticated" errors.
    const heal = () => {
      if (document.visibilityState === "hidden") return;
      void supabase.auth.getSession().then(({ data }) => {
        const expiresAt = data.session?.expires_at ?? 0;
        if (data.session && expiresAt * 1000 - Date.now() < 120_000) {
          void supabase.auth.refreshSession();
        }
      });
    };
    document.addEventListener("visibilitychange", heal);
    window.addEventListener("online", heal);

    return () => {
      sub.subscription.unsubscribe();
      document.removeEventListener("visibilitychange", heal);
      window.removeEventListener("online", heal);
    };
  }, []);

  return (
    <Ctx.Provider value={{
      user: session?.user ?? null,
      session,
      loading,
      signOut: async () => { await supabase.auth.signOut(); },
    }}>
      {children}
    </Ctx.Provider>
  );
}

export const useAuth = () => useContext(Ctx);
